// @phase13-shim — replaces 1,058-line legacy body after P5 cutover.
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { DesignerShellPage } from '../../designer';
import { useSceneStore } from '../../store/sceneStore';
import { sceneAPI } from '../../services/api';
import NativeVideoPlayer from '../../components/NativeVideoPlayer';
import TranscriptEditor from '../../components/scenes/TranscriptEditor';
import VersionHistoryPanel from '../../components/versioning/VersionHistoryPanel';

// ─── Chapter Markers editor ────────────────────────────────────────────────

function ChapterMarkersSection({ sceneId }) {
  const scene = useSceneStore((s) => s.scene);
  const setMeta = useSceneStore((s) => s.setMeta);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [newMarker, setNewMarker] = useState({ title: '', timestamp_ms: '', description: '' });

  if (!scene) return null;

  const markers = scene.chapter_markers || [];

  const persistMarkers = async (updatedMarkers) => {
    setMeta({ chapter_markers: updatedMarkers });
    setSaving(true);
    setSaveError(null);
    try {
      await sceneAPI.update(sceneId, { chapter_markers: updatedMarkers });
    } catch (err) {
      setSaveError('Save failed — changes may not persist until next reload.');
      console.error('[ChapterMarkersSection] save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddMarker = async () => {
    const title = newMarker.title.trim();
    const ms = parseInt(newMarker.timestamp_ms, 10);
    if (!title || isNaN(ms) || ms < 0) return;
    const marker = { title, timestamp_ms: ms };
    if (newMarker.description.trim()) marker.description = newMarker.description.trim();
    const updated = [...markers, marker].sort((a, b) => a.timestamp_ms - b.timestamp_ms);
    setNewMarker({ title: '', timestamp_ms: '', description: '' });
    await persistMarkers(updated);
  };

  const handleDeleteMarker = async (idx) => {
    const updated = markers.filter((_, i) => i !== idx);
    await persistMarkers(updated);
  };

  const formatMs = (ms) => {
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${min}:${String(sec).padStart(2, '0')}`;
  };

  return (
    <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 16 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'none',
          border: 'none',
          padding: '10px 0',
          fontWeight: 600,
          fontSize: 13,
          color: '#374151',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span style={{ fontSize: 11, color: '#6b7280' }}>{open ? '▼' : '▶'}</span>
        Chapter Markers
        <span style={{ marginLeft: 4, fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>
          ({markers.length})
        </span>
        {saving && <span style={{ marginLeft: 8, color: '#6366f1', fontSize: 11 }}>Saving…</span>}
      </button>

      {open && (
        <div style={{ paddingBottom: 16 }}>
          {saveError && (
            <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 8 }}>{saveError}</div>
          )}

          {markers.length === 0 ? (
            <div style={{ color: '#9ca3af', fontSize: 13, marginBottom: 12 }}>No chapter markers yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <th style={thStyle}>Time</th>
                  <th style={thStyle}>Title</th>
                  <th style={thStyle}>Description</th>
                  <th style={{ ...thStyle, width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {markers.map((m, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={tdStyle}>{formatMs(m.timestamp_ms)}</td>
                    <td style={tdStyle}>{m.title}</td>
                    <td style={{ ...tdStyle, color: '#6b7280' }}>{m.description || '—'}</td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => handleDeleteMarker(i)}
                        style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: '2px 6px' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <label style={labelStyle}>Time (ms)</label>
              <input
                type="number"
                min="0"
                placeholder="e.g. 30000"
                value={newMarker.timestamp_ms}
                onChange={(e) => setNewMarker((p) => ({ ...p, timestamp_ms: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 120 }}>
              <label style={labelStyle}>Title *</label>
              <input
                type="text"
                placeholder="Chapter title"
                value={newMarker.title}
                onChange={(e) => setNewMarker((p) => ({ ...p, title: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 2, minWidth: 140 }}>
              <label style={labelStyle}>Description (optional)</label>
              <input
                type="text"
                placeholder="Brief description"
                value={newMarker.description}
                onChange={(e) => setNewMarker((p) => ({ ...p, description: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <button
              onClick={handleAddMarker}
              disabled={!newMarker.title.trim() || newMarker.timestamp_ms === '' || saving}
              style={{
                background: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '6px 14px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                opacity: (!newMarker.title.trim() || newMarker.timestamp_ms === '' || saving) ? 0.5 : 1,
                height: 32,
                alignSelf: 'flex-end',
              }}
            >
              Add Marker
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = { textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 600, fontSize: 12 };
const tdStyle = { padding: '6px 8px', verticalAlign: 'top' };
const labelStyle = { fontSize: 11, color: '#6b7280', fontWeight: 500 };
const inputStyle = {
  border: '1px solid #d1d5db',
  borderRadius: 4,
  padding: '5px 8px',
  fontSize: 13,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

// ─── Video designer canvas ─────────────────────────────────────────────────

function VideoDesignCanvas() {
  const scene = useSceneStore((s) => s.scene);
  const updateScene = useSceneStore((s) => s.updateScene);
  const meta = scene?.domain_metadata || {};

  let videoUrl = meta.video_url || '';
  if (!videoUrl && meta.youtube_id) {
    videoUrl = `https://www.youtube.com/watch?v=${meta.youtube_id}`;
  }
  if (!videoUrl && meta.vimeo_id) {
    videoUrl = `https://vimeo.com/${meta.vimeo_id}`;
  }

  const handleTranscriptUpdate = (newSegments) => {
    if (updateScene && scene) {
      updateScene({
        ...scene,
        domain_metadata: {
          ...meta,
          transcript_segments: newSegments?.length ? newSegments : undefined,
        },
      });
    }
  };

  if (!videoUrl) {
    return (
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#0f1117', color: '#64748b', fontSize: '0.9rem', padding: '2rem',
        textAlign: 'center',
      }}>
        Paste a YouTube / Vimeo URL or upload a video to preview it here.
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#0a0a0f', minHeight: 0, overflow: 'auto' }}>
      <NativeVideoPlayer
        src={videoUrl}
        title={scene?.display_name || 'Video Preview'}
        sceneId={scene?.scene_id}
      />
      <div style={{ padding: '12px 16px', background: '#fff' }}>
        <TranscriptEditor
          sceneId={scene?.scene_id}
          segments={meta.transcript_segments}
          onUpdate={handleTranscriptUpdate}
        />
        <ChapterMarkersSection sceneId={scene?.scene_id} />
      </div>
    </div>
  );
}

export default function VideoSceneDesignerPage() {
  const { sceneId } = useParams();
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);

  const versionBtnStyle = {
    background: '#1e293b',
    color: '#94a3b8',
    border: '1px solid #334155',
    borderRadius: 6,
    padding: '5px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  };

  return (
    <div style={{ height: '100dvh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '6px 16px',
        background: '#0f172a',
        borderBottom: '1px solid #1e293b',
        flexShrink: 0,
      }}>
        <button style={versionBtnStyle} onClick={() => setVersionHistoryOpen(true)}>
          Version History
        </button>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <DesignerShellPage
          sceneId={sceneId}
          canvasSlot={<VideoDesignCanvas />}
          userRoles={[]}
          appSurface="coach"
        />
      </div>
      <VersionHistoryPanel
        sceneId={sceneId}
        isOpen={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        onRestore={() => window.location.reload()}
      />
    </div>
  );
}
